import React, { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from "../../../components/ui/dialog";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../../../components/ui/table";
import { Button } from "../../../components/ui/button";
import { TooltipWrapper } from "../../../components/ui/tooltip-wrapper";
import { Loading } from "@/components/src/animations/loading";
import { PanelRightOpen, SquareArrowOutUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ExtendedJobDocument } from "../../../lib/utils/calculate";
import { TableInvoiceRow } from "../types/invoice-table.types";
import {
	useSheetStore,
	SheetContentType,
} from "../../../store/Sheets/sheet.store";
import { useJobStore } from "../../../store/Jobs";
import { usePatientStore } from "../../../store/Patient";
import { showNotification } from "../../../store/Central/selectors";
import { Logger } from "@/lib/logger/Logger";
import { jobDocumentTypeToTagText } from "../../job-page/job-document/job-document.types";
import { formatCurrency } from "../../../lib/utils/utils-functions";
import type { JobWithShare } from "@/lib/supabase/supabaseTypes";

export interface InvoiceJobDocumentsDialogProps {
	isOpen: boolean;
	onClose: () => void;
	invoice: TableInvoiceRow | null;
}

const formatDocDate = (value?: string | null) => {
	if (!value) return "";
	return new Date(value).toLocaleDateString("de-CH", {
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
	});
};

export const InvoiceJobDocumentsDialog: React.FC<
	InvoiceJobDocumentsDialogProps
> = ({ isOpen, onClose, invoice }) => {
	const [loading, setLoading] = useState(false);
	const [documents, setDocuments] = useState<ExtendedJobDocument[]>([]);
	const navigate = useNavigate();

	const openSheet = useSheetStore((state) => state.openSheet);

	const { shareFile, unshareFile, acceptJobRequest, denyJobRequest } =
		useJobStore((state) => ({
			shareFile: state.shareFile,
			unshareFile: state.unshareFile,
			acceptJobRequest: state.acceptJobRequest,
			denyJobRequest: state.denyJobRequest,
		}));
	const patientName = usePatientStore((state) => state.patientName);

	useEffect(() => {
		if (!isOpen || !invoice?.invoice_id) {
			setDocuments([]);
			return;
		}

		const invoiceId = invoice.invoice_id;
		let cancelled = false;

		const fetchDocuments = async () => {
			setLoading(true);
			const { data, error } = await supabase
				.from("invoice_job_documents_view")
				.select("*")
				.eq("invoice_id", invoiceId)
				.order("job_document_date", { ascending: false });

			if (error) {
				Logger.error(
					"Failed to fetch invoice_job_documents_view",
					undefined,
					error
				);
				showNotification({
					message: "Fehler beim Laden der Auftragsdokumente",
					type: "error",
				});
				if (!cancelled) {
					setDocuments([]);
					setLoading(false);
				}
				return;
			}

			if (!cancelled) {
				setDocuments((data ?? []) as unknown as ExtendedJobDocument[]);
				setLoading(false);
			}
		};

		fetchDocuments();
		return () => {
			cancelled = true;
		};
	}, [isOpen, invoice?.invoice_id]);

	const handleOpenJobSheet = (doc: ExtendedJobDocument) => {
		openSheet(SheetContentType.JOB, {
			job: {
				id: doc.job_document_job_id,
				code: doc.job_code,
				title: doc.job_title,
				client_id: doc.job_client_id,
				status: doc.job_document_status,
				patient_code: doc.patient_code,
				tax_rate: doc.job_tax_rate,
			} as Partial<JobWithShare>,
			onShareFile: shareFile,
			onUnshareFile: unshareFile,
			onAcceptJobRequest: acceptJobRequest,
			onDenyJobRequest: denyJobRequest,
		});
	};

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<DialogContent
				className="max-w-3xl w-[90vw] p-0 gap-0 overflow-hidden"
				onInteractOutside={(e) => e.preventDefault()}
				aria-label="Auftragsdokumente der Rechnung"
				data-testid="invoice-job-documents-dialog"
			>
				<DialogHeader className="px-5 pt-4 pb-3 border-b">
					<DialogTitle className="text-base font-semibold">
						Auftragsdokumente
						{invoice?.invoice_doc_number ? (
							<span className="text-muted-foreground font-normal">
								{" "}
								· Rechnung {invoice.invoice_doc_number}
							</span>
						) : null}
					</DialogTitle>
					<DialogDescription className="text-xs">
						Alle Auftragsdokumente, aus denen diese Rechnung
						entstanden ist.
					</DialogDescription>
				</DialogHeader>
				<div className="max-h-[60vh] overflow-y-auto">
					{loading ? (
						<div className="flex items-center justify-center py-10">
							<Loading />
						</div>
					) : documents.length === 0 ? (
						<div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
							Keine Auftragsdokumente gefunden.
						</div>
					) : (
						<Table>
							<TableHeader className="sticky top-0 bg-background z-10">
								<TableRow>
									<TableHead className="w-[72px]"></TableHead>
									<TableHead>Auftrag</TableHead>
									<TableHead>Titel</TableHead>
									<TableHead className="w-[60px]">
										Typ
									</TableHead>
									<TableHead className="w-[110px]">
										Datum
									</TableHead>
									<TableHead>Patient</TableHead>
									<TableHead className="w-[110px] text-right">
										Betrag
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{documents.map((doc) => {
									const patient =
										doc.patient_first_name ||
										doc.patient_last_name
											? patientName({
													patient_id: "",
													first_name:
														doc.patient_first_name ??
														"",
													last_name:
														doc.patient_last_name ??
														"",
													code: "",
													title: "",
												})
											: "";
									const typeTag =
										jobDocumentTypeToTagText[
											doc.job_document_type as keyof typeof jobDocumentTypeToTagText
										] || doc.job_document_type;
									return (
										<TableRow
											key={doc.job_document_id}
											className="cursor-pointer"
											onClick={() =>
												handleOpenJobSheet(doc)
											}
										>
											<TableCell className="py-1">
												<div className="flex items-center gap-1">
													<TooltipWrapper title="Vorschau">
														<Button
															variant="ghost"
															size="sm"
															className="h-7 w-7 p-0"
															onClick={(e) => {
																e.stopPropagation();
																handleOpenJobSheet(
																	doc
																);
															}}
															aria-label="Auftragsvorschau öffnen"
														>
															<PanelRightOpen className="h-4 w-4" />
														</Button>
													</TooltipWrapper>
													<TooltipWrapper title="Zum Auftrag">
														<Button
															variant="ghost"
															size="sm"
															className="h-7 w-7 p-0"
															onClick={(e) => {
																e.stopPropagation();
																onClose();
																navigate(
																	`/client/${doc.job_client_id}/job/${doc.job_document_job_id}`
																);
															}}
															aria-label="Zum Auftrag"
														>
															<SquareArrowOutUpRight className="h-4 w-4" />
														</Button>
													</TooltipWrapper>
												</div>
											</TableCell>
											<TableCell className="font-medium">
												{doc.job_code}
											</TableCell>
											<TableCell>
												{doc.job_title}
											</TableCell>
											<TableCell>{typeTag}</TableCell>
											<TableCell>
												{formatDocDate(
													doc.job_document_date
												)}
											</TableCell>
											<TableCell className="truncate max-w-[200px]">
												{patient}
											</TableCell>
											<TableCell className="text-right font-mono">
												{doc.job_document_amount != null
													? formatCurrency(
															doc.job_document_amount,
															doc.job_currency_text ??
																undefined
														)
													: ""}
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
};
